import React, { useEffect, useState } from "react";
import { Wrench, Clock } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";

const POLL_MS = 60000;

const remaining = (iso) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  const mins = Math.floor(ms / 60000);
  return { h: Math.floor(mins / 60), m: mins % 60, s: Math.floor((ms % 60000) / 1000) };
};

const when = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

const Countdown = ({ backAt }) => {
  const { t } = useLang();
  const [left, setLeft] = useState(() => remaining(backAt));
  useEffect(() => {
    setLeft(remaining(backAt));
    const id = setInterval(() => setLeft(remaining(backAt)), 1000);
    return () => clearInterval(id);
  }, [backAt]);
  if (!backAt) return null;
  if (!left) return <p className="text-emerald-200 font-semibold" data-testid="maintenance-soon">{t("mt.soon")}</p>;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid="maintenance-countdown">
      <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-emerald-300/80">{t("mt.backIn")}</p>
      <p className="mt-1 font-mono text-4xl sm:text-5xl font-extrabold tabular-nums text-white">
        {pad(left.h)}:{pad(left.m)}:{pad(left.s)}
      </p>
      <p className="mt-1 text-xs text-emerald-100/70">{t("mt.backAt", { time: when(backAt) })}</p>
    </div>
  );
};

const MaintenanceScreen = ({ state }) => {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#08150e] text-white flex items-center justify-center p-6" data-testid="maintenance-screen">
      <div className="relative w-full max-w-xl overflow-hidden rounded-3xl bg-[#0f2a1a] p-8 sm:p-10">
        <div className="absolute -right-20 -top-24 h-64 w-64 rounded-full bg-[#16a34a]/40 blur-3xl" />
        <div className="relative space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-emerald-200 backdrop-blur">
            <Wrench size={13} /> LytePress
          </span>
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("mt.title")}</h1>
            <p className="mt-3 text-sm text-emerald-100/85" data-testid="maintenance-message">
              {state.message || t("mt.body")}
            </p>
          </div>
          <Countdown backAt={state.backAt} />
        </div>
      </div>
    </div>
  );
};

const Bar = ({ tone, children, testId }) => (
  <div data-testid={testId}
    className={`sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-2 px-4 py-2 text-xs font-bold ${tone}`}>
    {children}
  </div>
);

// Planned downtime, felt by everyone at once: the server blocks the writes, this decides what the
// person staring at the screen sees while it does.
export const MaintenanceGate = ({ children }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/maintenance")
      .then((r) => { if (alive) setState(r.data); })
      .catch(() => {});
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const isAdmin = user?.role === "admin";
  const staffPortal = window.location.pathname.startsWith("/staff-login");
  if (state?.on && state.mode === "full" && !isAdmin && !staffPortal) {
    return <MaintenanceScreen state={state} />;
  }

  return (
    <>
      {state?.on && isAdmin && (
        <Bar tone="bg-rose-600 text-white" testId="maintenance-admin-bar">
          <Wrench size={13} /> {t("mt.adminOn")}
        </Bar>
      )}
      {state?.on && !isAdmin && (
        <Bar tone="bg-[#b45309] text-white" testId="maintenance-readonly-bar">
          <Wrench size={13} /> {state.message || t("mt.readonly")}
          {state.backAt ? ` · ${t("mt.backAt", { time: when(state.backAt) })}` : ""}
        </Bar>
      )}
      {!state?.on && state?.notice && (
        <Bar tone="bg-[#fdf6e7] text-[#92400e] dark:bg-[#33270f] dark:text-[#fcd34d]" testId="maintenance-notice-bar">
          <Clock size={13} /> {t("mt.notice", { when: when(state.startsAt) })}
        </Bar>
      )}
      {children}
    </>
  );
};

export default MaintenanceGate;
